import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Edit,
  Copy,
  Trash2,
  MoreVertical,
  CheckCircle,
  XCircle,
  Plane,
  Hotel,
  Car,
  Compass,
  UtensilsCrossed,
  Building2,
  Plus,
  MapPin,
  Calendar,
  Users,
  Phone,
  Mail,
  Loader2
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const serviceIcons = {
  flight: Plane,
  hotel: Hotel,
  transfer: Car,
  assistance: Users,
  tour: Compass,
  meal: UtensilsCrossed,
  conference: Building2,
  other: Plus
};

// Labels for service types in Spanish
const serviceTypeLabels = {
  flight: "Vuelos",
  hotel: "Hoteles",
  transfer: "Traslados",
  assistance: "Asistencia al Viajero",
  tour: "Tours",
  meal: "Alimentos",
  conference: "Salones / Conferencias",
  other: "Otros Servicios"
};

const roomTypeLabels = {
  sencilla: "Sencilla",
  doble: "Doble",
  triple: "Triple",
  cuadruple: "Cuádruple",
  suite: "Suite",
  suite_junior: "Suite Junior",
  suite_presidencial: "Suite Presidencial",
  villa: "Villa",
  bungalow: "Bungalow",
  familiar: "Familiar",
  deluxe: "Deluxe",
  superior: "Superior",
  estandar: "Estándar"
};

const flightTypeLabels = {
  roundtrip: "Viaje Redondo",
  oneway: "Solo Ida"
};

const coverageTypeLabels = {
  basica: "Básica",
  estandar: "Estándar",
  premium: "Premium",
  todo_riesgo: "Todo Riesgo",
  medica: "Médica",
  cancelacion: "Cancelación de Viaje"
};

const QuotePreview = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const pdfRef = useRef();
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);
  const [clientLogoBase64, setClientLogoBase64] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Convert logo URL to base64 for PDF generation
  useEffect(() => {
    if (settings?.logo_url) {
      convertImageToBase64(settings.logo_url, setLogoBase64);
    }
  }, [settings?.logo_url]);

  // Convert client logo URL to base64 for PDF generation
  useEffect(() => {
    if (quote?.client_logo_url) {
      convertImageToBase64(quote.client_logo_url, setClientLogoBase64);
    }
  }, [quote?.client_logo_url]);

  const convertImageToBase64 = async (url, setBase64) => {
    try {
      // Build full URL using current origin
      const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      
      const response = await fetch(fullUrl, { 
        credentials: 'include',
        mode: 'cors'
      });
      
      if (!response.ok) {
        console.error("Failed to fetch logo:", response.status);
        return;
      }
      
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setBase64(reader.result);
      };
      reader.onerror = () => {
        console.error("Error reading logo blob");
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("Error converting logo to base64:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [quoteRes, settingsRes] = await Promise.all([
        axios.get(`${API}/quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      let quoteData = quoteRes.data;
      
      // Si la cotización no tiene client_logo_url, buscar el logo del cliente
      if (!quoteData.client_logo_url) {
        try {
          // Primero intentar por client_id
          if (quoteData.client_id) {
            const clientRes = await axios.get(`${API}/clients/${quoteData.client_id}`, { 
              headers: { Authorization: `Bearer ${token}` } 
            });
            if (clientRes.data?.logo_url) {
              quoteData = { ...quoteData, client_logo_url: clientRes.data.logo_url };
            }
          } 
          // Si no hay client_id, buscar por nombre de empresa o nombre del cliente
          if (!quoteData.client_logo_url) {
            const searchTerm = quoteData.client_company || quoteData.client_name;
            if (searchTerm) {
              const searchRes = await axios.get(`${API}/clients/search`, { 
                params: { q: searchTerm },
                headers: { Authorization: `Bearer ${token}` } 
              });
              // Buscar coincidencia (exacta o parcial)
              const matchingClient = searchRes.data?.find(c => {
                const companyMatch = c.company_name?.toUpperCase().trim() === searchTerm?.toUpperCase().trim();
                const contactMatch = c.contact_name?.toUpperCase().trim() === searchTerm?.toUpperCase().trim();
                return companyMatch || contactMatch;
              });
              if (matchingClient?.logo_url) {
                quoteData = { ...quoteData, client_logo_url: matchingClient.logo_url };
              }
            }
          }
        } catch (clientError) {
          // Client logo not found, non-critical
        }
      }
      
      setQuote(quoteData);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error("Error al cargar la cotización");
      navigate("/historial");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!pdfRef.current) {
      toast.error("Error: No se encontró el contenido para generar el PDF");
      return;
    }
    
    setDownloading(true);
    toast.info("Generando PDF, espera un momento...");
    
    try {
      const element = pdfRef.current;
      
      
      
      // Capturar el elemento como canvas
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000,
      });
      
      
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas tiene dimensiones inválidas");
      }
      
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      
      // Dimensiones A4 en mm
      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 8;
      const marginBottom = 15;
      
      // Ancho disponible para contenido
      const contentWidth = pdfWidth - (margin * 2);
      
      // Calcular altura proporcional de la imagen
      const imgRatio = canvas.height / canvas.width;
      const scaledImgHeight = contentWidth * imgRatio;
      
      // Altura disponible por página
      const pageContentHeight = pdfHeight - margin - marginBottom;
      
      // Overlap en pixels del canvas (aproximadamente 40px para cubrir una línea de texto)
      const overlapPx = 60;
      
      // Crear PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      let currentY = 0; // Posición actual en el canvas
      let pageNum = 0;
      
      while (currentY < canvas.height) {
        if (pageNum > 0) {
          pdf.addPage();
        }
        
        // Calcular altura de esta porción
        const remainingHeight = canvas.height - currentY;
        const sourceHeight = Math.min(
          (pageContentHeight / scaledImgHeight) * canvas.height,
          remainingHeight
        );
        
        // Crear canvas temporal para esta porción
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        
        ctx.drawImage(
          canvas,
          0, currentY,
          canvas.width, sourceHeight,
          0, 0,
          pageCanvas.width, pageCanvas.height
        );
        
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.92);
        const pageImgHeight = (pageCanvas.height / canvas.width) * contentWidth;
        
        pdf.addImage(
          pageImgData,
          'JPEG',
          margin,
          margin,
          contentWidth,
          pageImgHeight
        );
        
        // Avanzar posición, restando overlap para la siguiente página
        // (excepto si es la última porción)
        if (currentY + sourceHeight >= canvas.height) {
          break; // Última página, salir
        }
        
        currentY += sourceHeight - overlapPx;
        pageNum++;
        
        // Seguridad para evitar loop infinito
        if (pageNum > 30) break;
      }
      
      
      
      // Generar nombre del archivo
      const filename = quote?.project_name 
        ? `${quote.project_name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '_')}_${quote.quote_number}.pdf`
        : `${quote?.quote_number || 'cotizacion'}.pdf`;
      
      pdf.save(filename);
      toast.success("PDF descargado exitosamente");
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar el PDF. Intenta de nuevo.");
    } finally {
      setDownloading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/quotes/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuote(prev => ({ ...prev, status: newStatus }));
      toast.success(`Estado actualizado a ${newStatus}`);
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDuplicate = async () => {
    try {
      const res = await axios.post(`${API}/quotes/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cotización duplicada");
      navigate(`/editar/${res.data.id}`);
    } catch (error) {
      toast.error("Error al duplicar");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de eliminar esta cotización?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cotización eliminada");
      navigate("/historial");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const calculateExpirationDate = (createdAt, validityDays) => {
    if (!createdAt) return "—";
    const date = new Date(createdAt);
    date.setDate(date.getDate() + (validityDays || 15));
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Función para convertir número a texto en español
  const numberToWords = (num) => {
    const units = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const teens = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
    const tens = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const hundreds = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    if (num === 0) return 'CERO';
    if (num === 100) return 'CIEN';

    const convertGroup = (n) => {
      if (n === 0) return '';
      if (n < 10) return units[n];
      if (n < 20) return teens[n - 10];
      if (n < 30) {
        if (n === 20) return 'VEINTE';
        return 'VEINTI' + units[n % 10];
      }
      if (n < 100) {
        const t = Math.floor(n / 10);
        const u = n % 10;
        return tens[t] + (u ? ' Y ' + units[u] : '');
      }
      if (n === 100) return 'CIEN';
      if (n < 1000) {
        const h = Math.floor(n / 100);
        const rest = n % 100;
        return hundreds[h] + (rest ? ' ' + convertGroup(rest) : '');
      }
      return '';
    };

    const integer = Math.floor(num);
    const decimal = Math.round((num - integer) * 100);

    let result = '';

    if (integer >= 1000000) {
      const millions = Math.floor(integer / 1000000);
      const rest = integer % 1000000;
      if (millions === 1) {
        result = 'UN MILLÓN';
      } else {
        result = convertGroup(millions) + ' MILLONES';
      }
      if (rest > 0) {
        if (rest >= 1000) {
          const thousands = Math.floor(rest / 1000);
          const remainder = rest % 1000;
          if (thousands === 1) {
            result += ' MIL';
          } else {
            result += ' ' + convertGroup(thousands) + ' MIL';
          }
          if (remainder > 0) {
            result += ' ' + convertGroup(remainder);
          }
        } else {
          result += ' ' + convertGroup(rest);
        }
      }
    } else if (integer >= 1000) {
      const thousands = Math.floor(integer / 1000);
      const rest = integer % 1000;
      if (thousands === 1) {
        result = 'MIL';
      } else {
        result = convertGroup(thousands) + ' MIL';
      }
      if (rest > 0) {
        result += ' ' + convertGroup(rest);
      }
    } else {
      result = convertGroup(integer);
    }

    if (decimal > 0) {
      result += ' ' + decimal.toString().padStart(2, '0') + '/100';
    } else {
      result += ' 00/100';
    }

    return result.trim();
  };

  const getCurrencyName = (currency) => {
    const names = {
      'MXN': 'MONEDA NACIONAL',
      'USD': 'DÓLARES AMERICANOS',
      'EUR': 'EUROS'
    };
    return names[currency] || currency;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: quote?.currency || "USD"
    }).format(amount || 0);
  };

  // Calculate summary by service type
  const getServiceSummaryByType = () => {
    if (!quote?.services?.length) return [];
    
    const summaryMap = {};
    
    quote.services.forEach(service => {
      const type = service.type || 'other';
      if (!summaryMap[type]) {
        summaryMap[type] = {
          type,
          label: serviceTypeLabels[type] || type,
          subtotal: 0,
          iva: 0,
          total: 0
        };
      }
      
      const unitPrice = service.unit_price || 0;
      const ivaPercentage = service.service_iva_percentage ?? 16;
      const ivaAmount = service.service_iva_amount || (unitPrice * (ivaPercentage / 100));
      const priceWithIva = unitPrice + ivaAmount;
      
      summaryMap[type].subtotal += unitPrice;
      summaryMap[type].iva += ivaAmount;
      summaryMap[type].total += priceWithIva;
    });
    
    // Convert to array and maintain order of appearance
    const typeOrder = [];
    quote.services.forEach(service => {
      const type = service.type || 'other';
      if (!typeOrder.includes(type)) {
        typeOrder.push(type);
      }
    });
    
    return typeOrder.map(type => summaryMap[type]);
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: "Borrador", className: "bg-slate-100 text-slate-600" },
      sent: { label: "Enviada", className: "bg-sky-100 text-sky-600" },
      accepted: { label: "Aceptada", className: "bg-emerald-100 text-emerald-600" },
      rejected: { label: "Rechazada", className: "bg-red-100 text-red-600" }
    };
    const c = config[status] || config.draft;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const renderServiceDetails = (service) => {
    const type = service.type;
    const details = [];

    if (type === "flight") {
      // Tipo de vuelo
      if (service.flight_type) {
        details.push({
          label: "Tipo",
          value: flightTypeLabels[service.flight_type] || service.flight_type
        });
      }
      // Ruta
      if (service.flight_origin || service.flight_destination) {
        details.push({
          label: "Ruta",
          value: `${service.flight_origin || "—"} → ${service.flight_destination || "—"}`
        });
      }
      // Fecha Salida
      if (service.flight_start_date) {
        details.push({
          label: "Fecha Salida",
          value: formatDate(service.flight_start_date)
        });
      }
      // Fecha Regreso (solo si es roundtrip)
      if (service.flight_type !== "oneway" && service.flight_end_date) {
        details.push({
          label: "Fecha Regreso",
          value: formatDate(service.flight_end_date)
        });
      }
      // PAX
      if (service.flight_pax > 0) {
        details.push({
          label: "Pasajeros",
          value: `${service.flight_pax} PAX`
        });
      }
      // Precio Unitario (por PAX)
      if (service.flight_price_per_pax > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(service.flight_price_per_pax)
        });
      }
    } else if (type === "hotel") {
      // Tipo de Habitación
      if (service.hotel_room_type) {
        details.push({
          label: "Tipo Habitación",
          value: roomTypeLabels[service.hotel_room_type] || service.hotel_room_type
        });
      }
      // Cantidad de habitaciones
      if (service.hotel_room_count > 0) {
        details.push({
          label: "Habitaciones",
          value: `${service.hotel_room_count}`
        });
      }
      // Fechas
      if (service.hotel_start_date) {
        details.push({
          label: "Check-in",
          value: formatDate(service.hotel_start_date)
        });
      }
      if (service.hotel_end_date) {
        details.push({
          label: "Check-out",
          value: formatDate(service.hotel_end_date)
        });
      }
      // Noches
      if (service.hotel_nights > 0) {
        details.push({
          label: "Noches",
          value: `${service.hotel_nights}`
        });
      }
      // Precio Unitario (por noche)
      if (service.hotel_price_per_night > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(service.hotel_price_per_night)
        });
      }
    } else if (type === "transfer") {
      // Tipo de vehículo
      if (service.transfer_vehicle_type) {
        details.push({
          label: "Vehículo",
          value: service.transfer_vehicle_type
        });
      }
      // Ruta
      if (service.transfer_origin || service.transfer_destination) {
        details.push({
          label: "Ruta",
          value: `${service.transfer_origin || "—"} → ${service.transfer_destination || "—"}`
        });
      }
      // Fecha Inicio
      if (service.transfer_start_date) {
        details.push({
          label: "Fecha Inicio",
          value: formatDate(service.transfer_start_date)
        });
      }
      // Fecha Fin
      if (service.transfer_end_date) {
        details.push({
          label: "Fecha Fin",
          value: formatDate(service.transfer_end_date)
        });
      }
      // Cantidad
      if (service.quantity > 1) {
        details.push({
          label: "Cantidad",
          value: `${service.quantity}`
        });
      }
      // Precio Unitario
      const unitPrice = (service.unit_price || 0) / (service.quantity || 1);
      if (unitPrice > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(unitPrice)
        });
      }
    } else if (type === "assistance") {
      // Tipo de cobertura
      if (service.assistance_coverage_type) {
        details.push({
          label: "Cobertura",
          value: coverageTypeLabels[service.assistance_coverage_type] || service.assistance_coverage_type
        });
      }
      // Destino
      if (service.assistance_destination) {
        details.push({
          label: "Destino",
          value: service.assistance_destination
        });
      }
      // Fecha Inicio
      if (service.assistance_start_date) {
        details.push({
          label: "Fecha Inicio",
          value: formatDate(service.assistance_start_date)
        });
      }
      // Fecha Fin
      if (service.assistance_end_date) {
        details.push({
          label: "Fecha Fin",
          value: formatDate(service.assistance_end_date)
        });
      }
      // Cantidad
      if (service.quantity > 1) {
        details.push({
          label: "Cantidad",
          value: `${service.quantity}`
        });
      }
      // Precio Unitario
      const unitPrice = (service.unit_price || 0) / (service.quantity || 1);
      if (unitPrice > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(unitPrice)
        });
      }
    } else {
      // Otros servicios genéricos
      if (service.quantity > 1) {
        details.push({
          label: "Cantidad",
          value: `${service.quantity}`
        });
      }
      // Precio Unitario
      const unitPrice = (service.unit_price || 0) / (service.quantity || 1);
      if (unitPrice > 0) {
        details.push({
          label: "Precio Unitario",
          value: formatCurrency(unitPrice)
        });
      }
    }

    return details;
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  const totalPassengers = (quote?.passengers?.adults || 0) + 
                          (quote?.passengers?.children || 0) + 
                          (quote?.passengers?.infants || 0);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="quote-preview-page">
      <div className="max-w-5xl mx-auto">
      {/* Header - No Print */}
      <div className="flex items-center justify-between mb-8 no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/historial")} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold text-slate-900">{quote?.quote_number}</h1>
              {getStatusBadge(quote?.status)}
            </div>
            <p className="text-slate-500">Creada el {formatDate(quote?.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleDownloadPDF} 
            disabled={downloading}
            data-testid="download-button"
            className="bg-amber-500 text-white hover:bg-amber-600 border-amber-500"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Descargar PDF
          </Button>
          <Button variant="outline" onClick={() => navigate(`/editar/${id}`)} data-testid="edit-button">
            <Edit className="w-4 h-4 mr-2" />Editar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="more-actions-button">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleStatusChange("accepted")}>
                <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />Marcar Aceptada
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange("rejected")}>
                <XCircle className="w-4 h-4 mr-2 text-red-500" />Marcar Rechazada
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDuplicate}>
                <Copy className="w-4 h-4 mr-2" />Duplicar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" />Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Quote Document - For PDF */}
      <Card className="pdf-preview max-w-4xl mx-auto overflow-hidden" ref={pdfRef}>
        {/* Header with PD Corporate Colors */}
        <div className="text-white p-8" style={{ background: 'linear-gradient(135deg, #00157f, #0037ff)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {(logoBase64 || settings?.logo_url) ? (
                <div style={{ marginBottom: '16px' }}>
                  <img 
                    src={logoBase64 || settings.logo_url} 
                    alt="Logo PD Travel" 
                    style={{ height: '56px', objectFit: 'contain', maxWidth: '200px' }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#04ccda', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plane style={{ width: '24px', height: '24px', color: 'white' }} />
                  </div>
                  <span style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: "'Mulish', sans-serif", color: 'white' }}>{settings?.name || "PD Travel"}</span>
                </div>
              )}
              <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '8px', fontFamily: "'Great', cursive", color: 'white' }}>
                Cotización de Viaje
              </h1>
              <p style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '18px' }}>{quote?.quote_number}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6" style={{ fontFamily: "'Mulish', sans-serif" }}>
          {/* Client & Agency Info */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#00157f' }}>Cliente</h3>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                <div>
                  {quote?.client_company && (
                    <p className="font-bold text-lg text-slate-900">{quote.client_company}</p>
                  )}
                  {quote?.business_unit && (
                    <p className="text-sm text-slate-600 font-medium">{quote.business_unit}</p>
                  )}
                  <p className={`${quote?.client_company ? 'text-slate-600' : 'font-semibold text-lg text-slate-900'}`}>
                    {quote?.client_name}
                  </p>
                </div>
                {(clientLogoBase64 || quote?.client_logo_url) && (
                  <img 
                    src={clientLogoBase64 || (quote.client_logo_url.startsWith('http') ? quote.client_logo_url : `${window.location.origin}${quote.client_logo_url}`)}
                    alt="Logo del cliente"
                    style={{ maxHeight: '48px', maxWidth: '100px', width: 'auto', height: 'auto', objectFit: 'contain', marginLeft: '50px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              {quote?.client_email && (
                <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px', lineHeight: '1.4' }}>
                  <span style={{ fontWeight: 'bold', marginRight: '3px' }}>@</span> {quote.client_email}
                </p>
              )}
              {quote?.client_phone && (
                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.4' }}>
                  <span style={{ fontSize: '13px', marginRight: '3px' }}>☏</span> {quote.client_phone}
                </p>
              )}
              {/* Fechas de cotización */}
              <div className="mt-4 pt-3 border-t border-slate-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-slate-400 text-xs">Fecha de Cotización</p>
                    <p className="font-medium text-slate-700">{formatDate(quote?.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Fecha de Vencimiento</p>
                    <p className="font-medium text-amber-600">{calculateExpirationDate(quote?.created_at, quote?.validity_days)}</p>
                  </div>
                </div>
                {quote?.project_name && (
                  <p className="font-bold mt-3 uppercase" style={{ color: '#0037ff', fontSize: '1.35rem' }}>
                    PROYECTO: {quote.project_name.toUpperCase()}
                  </p>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Agencia</h3>
              <div>
                <p className="font-bold text-lg text-slate-900">{settings?.name || "PD Travel"}</p>
                {settings?.rfc && (
                  <p className="text-sm text-slate-600">RFC: {settings.rfc}</p>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-slate-400 text-xs">Cotización Realizada por</p>
                <p className="font-medium text-slate-700">{quote?.created_by_name || quote?.user_name || quote?.assigned_user_name || "—"}</p>
                {(quote?.created_by_email || quote?.user_email) && (
                  <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', lineHeight: '1.4' }}>
                    <span style={{ fontWeight: 'bold', marginRight: '3px' }}>@</span> {quote?.created_by_email || quote?.user_email}
                  </p>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Destination, Dates & Passengers */}
          <div className="grid grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Destino</p>
                <p className="font-medium">{quote?.destination}</p>
                {quote?.origin && <p className="text-xs text-slate-400">Desde: {quote.origin}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Fecha Inicio</p>
                <p className="font-medium">{formatDate(quote?.start_date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Fecha Fin</p>
                <p className="font-medium">{formatDate(quote?.end_date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pasajeros</p>
                <p className="font-medium">{totalPassengers} persona{totalPassengers !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Services with detailed info */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#00157f' }}>Servicios Incluidos</h3>
            {quote?.services?.length > 0 ? (
              <div className="space-y-3">
                {quote.services.map((service, index) => {
                  const Icon = serviceIcons[service.type] || Plus;
                  const details = renderServiceDetails(service);
                  const serviceIvaPercentage = service.service_iva_percentage ?? 16;
                  const serviceIvaAmount = service.service_iva_amount || (service.unit_price * (serviceIvaPercentage / 100));
                  const priceWithIva = (service.unit_price || 0) + serviceIvaAmount;
                  
                  return (
                    <div key={service.id || index} className="p-4 rounded-lg pdf-no-break" style={{ background: '#f8fafc', borderLeft: '4px solid #0093e8' }}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0" style={{ border: '1px solid #e2e8f0' }}>
                            <Icon className="w-5 h-5" style={{ color: '#1d6f80' }} />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">
                              {service.description || service.provider || service.type}
                            </p>
                            {service.provider && service.description && (
                              <p className="text-sm text-slate-500">{service.provider}</p>
                            )}
                            
                            {details.length > 0 && (
                              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                                {details.map((detail, idx) => (
                                  <div key={idx} className="text-sm">
                                    <span className="text-slate-400">{detail.label}: </span>
                                    <span className="text-slate-700 font-medium">{detail.value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className="text-sm text-slate-500">{formatCurrency(service.unit_price || 0)}</p>
                          <p className="text-xs text-slate-400">IVA {serviceIvaPercentage}%: {formatCurrency(serviceIvaAmount)}</p>
                          <p className="font-bold text-lg mt-1" style={{ color: '#00157f' }}>{formatCurrency(priceWithIva)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No hay servicios</p>
            )}
          </div>

          {/* Service Summary by Type */}
          {quote?.services?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#00157f' }}>Resumen por Tipo de Servicio</h3>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#00157f' }}>
                      <th style={{ textAlign: 'left', padding: '16px', color: 'white', fontSize: '14px', fontWeight: '600', verticalAlign: 'middle' }}>Servicio</th>
                      <th style={{ textAlign: 'right', padding: '16px', color: 'white', fontSize: '14px', fontWeight: '600', verticalAlign: 'middle' }}>Subtotal</th>
                      <th style={{ textAlign: 'right', padding: '16px', color: 'white', fontSize: '14px', fontWeight: '600', verticalAlign: 'middle' }}>IVA</th>
                      <th style={{ textAlign: 'right', padding: '16px', color: 'white', fontSize: '14px', fontWeight: '600', verticalAlign: 'middle' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getServiceSummaryByType().map((summary, index) => {
                      const Icon = serviceIcons[summary.type] || Plus;
                      return (
                        <tr key={summary.type} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                          <td style={{ padding: '16px', paddingTop: '25px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Icon style={{ width: '16px', height: '16px', color: '#0093e8', flexShrink: 0 }} />
                              <span style={{ fontWeight: '500', color: '#334155' }}>{summary.label}</span>
                            </div>
                          </td>
                          <td style={{ padding: '16px', paddingTop: '25px', textAlign: 'right', color: '#475569', verticalAlign: 'middle' }}>{formatCurrency(summary.subtotal)}</td>
                          <td style={{ padding: '16px', paddingTop: '25px', textAlign: 'right', color: '#475569', verticalAlign: 'middle' }}>{formatCurrency(summary.iva)}</td>
                          <td style={{ padding: '16px', paddingTop: '25px', textAlign: 'right', fontWeight: '600', color: '#00157f', verticalAlign: 'middle' }}>{formatCurrency(summary.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <Separator />

          {/* Totals with IVA breakdown - with page break avoidance */}
          <div 
            className="rounded-xl p-6" 
            style={{ 
              background: 'linear-gradient(135deg, #00157f, #0037ff)',
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid'
            }}
          >
            <div className="flex justify-between items-center text-white mb-3">
              <span className="text-xl text-cyan-200">Subtotal</span>
              <span className="text-xl font-semibold">{formatCurrency(quote?.subtotal || quote?.total || 0)}</span>
            </div>
            {quote?.iva_amount > 0 && (
              <div className="flex justify-between items-center text-cyan-200 mb-5">
                <span className="text-lg">IVA</span>
                <span className="text-lg font-semibold">{formatCurrency(quote?.iva_amount || 0)}</span>
              </div>
            )}
            <div className="border-t border-blue-400/50 mb-5"></div>
            <div className="flex justify-between items-center text-white pt-4">
              <span className="text-3xl font-bold">Total</span>
              <span className="text-3xl font-bold" style={{ color: '#04ccda' }}>{formatCurrency(quote?.total)}</span>
            </div>
            <div className="border-t border-blue-400/50 mt-1 pt-3">
              <p className="text-xs text-cyan-200 uppercase tracking-wide text-center">
                ({numberToWords(quote?.total || 0)} {getCurrencyName(quote?.currency)})
              </p>
            </div>
          </div>

          {/* Terms */}
          {quote?.terms && (
            <div className="mt-8 pdf-no-break">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#00157f' }}>Términos y Condiciones de Emisión y Servicio</h3>
              <div 
                className="text-sm text-slate-600 p-4 rounded-lg terms-content" 
                style={{ background: '#f8fafc', fontFamily: "'Mulish', sans-serif", lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quote.terms) }}
              />
            </div>
          )}

          {/* Validity */}
          <div className="text-center text-sm text-slate-500 pt-4 border-t pdf-no-break" style={{ fontFamily: "'Mulish', sans-serif" }}>
            <p>Esta cotización es válida por <strong>{quote?.validity_days || 15} días</strong> (vence el <strong>{calculateExpirationDate(quote?.created_at, quote?.validity_days)}</strong>)</p>
            {settings?.phone && <p className="mt-2">Contacto: {settings.phone} | {settings.email}</p>}
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
};

export default QuotePreview;
